/* istanbul ignore file */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Label } from '@bingads-webui-react/primitive';
import { Stack } from '@bingads-webui-react/fabric-control';
import { DateTimePicker } from '@bingads-webui-react/date-time-picker';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { Error } from './error';
import { AdjustmentType, isDataExclusion } from '../adjustment-type';

function getStyles() {
  return {
    seasonalityDateTimePicker: {
      marginBottom: '16px',
      '& div[role=alert]': {
        paddingTop: 5,
      },
    },
  };
}

const getDifferenceInDays = (date1, date2) => {
  const diffInMs = (date2 - date1);
  return diffInMs / (1000 * 60 * 60 * 24);
};

// validation
// start date > end date
// 0 < range < 14
const getErrorMessage = (selectedStartDate, selectedStartTime, selectedEndDate, selectedEndTime) => {
  if (!selectedStartDate || !selectedStartTime || !selectedEndDate || !selectedEndTime) {
    return _TL_('Time is required. Select a time.');
  }

  const startDateTime = new Date(selectedStartDate).setHours(selectedStartTime);
  const endDateTime = new Date(selectedEndDate).setHours(selectedEndTime);

  if (startDateTime >= endDateTime) return _TL_('Date range is not valid. End date should be after the start date.');
  if (getDifferenceInDays(startDateTime, endDateTime) > 14) return _TL_('Date range is not valid. Enter a date range that is 14 days or less.');

  return _TL_('');
};

export function validateDateTimeRange(selectedStartDate, selectedStartTime, selectedEndDate, selectedEndTime) {
  return _.isEmpty(getErrorMessage(selectedStartDate, selectedStartTime, selectedEndDate, selectedEndTime));
}

function getDate(date) {
  const newDate = new Date(Date.UTC(date.year, date.month - 1, date.day, 0, 0, 0));

  return newDate;
}

function SeasonalityDateTimePicker(props) {
  const {
    i18n, classes, selectedStartDate, setSelectedStartDate,
    selectedEndDate, setSelectedEndDate, selectedStartTime,
    setSelectedStartTime, selectedEndTime, setSelectedEndTime,
    showValidationError, adjustmentType,
  } = props;

  const onStartDateChange = useCallback((date) => {
    setSelectedStartDate(getDate(date));
  }, [setSelectedStartDate]);

  const onEndDateChange = useCallback((date) => {
    setSelectedEndDate(getDate(date));
  }, [setSelectedEndDate]);

  const onStartTimeChange = useCallback(
    option => setSelectedStartTime(option.key),
    [setSelectedStartTime]
  );

  const onEndTimeChange = useCallback(
    option => setSelectedEndTime(option.key),
    [setSelectedEndTime]
  );

  const errorMessage = useMemo(
    () => (
      showValidationError ?
        i18n.getString(getErrorMessage(selectedStartDate, selectedStartTime, selectedEndDate, selectedEndTime)) : ''),
    [i18n, selectedEndDate, selectedEndTime, selectedStartDate, selectedStartTime, showValidationError]
  );

  return (
    <div className={classNames('seasonality-datetime-picker', classes.seasonalityDateTimePicker)}>
      <Stack
        horizontal
        tokens={{ childrenGap: 50 }}
        className="datetimepicker-stack-container"
      >
        <div className="start-date-picker">
          <Label id="start-time-label">
            {i18n.getString(_TL_('Start time'))}
            <HelpPopupInline
              topic={isDataExclusion(adjustmentType) ? 'pop_BA_DataExclusions_StartTime' : 'pop_BA_SeasonalityAdjustments_StartTime'}
              ariaLabel={i18n.getString(_TL_('Click to learn more about {{item}}', { item: i18n.getString(_TL_('Start time')) }))}
            />
          </Label>
          <DateTimePicker
            i18n={i18n}
            onDateChange={onStartDateChange}
            selectedDate={selectedStartDate}
            onTimeChange={onStartTimeChange}
            selectedHour={selectedStartTime}
            hideTitle
            datePickerId="StartDatePicker"
            timePickerId="StartTimePicker"
          />
        </div>
        <div className="end-date-picker">
          <Label id="end-time-label">
            {i18n.getString(_TL_('End time'))}
            <HelpPopupInline
              topic={isDataExclusion(adjustmentType) ? 'pop_BA_DataExclusions_EndTime' : 'pop_BA_SeasonalityAdjustments_EndTime'}
              ariaLabel={i18n.getString(_TL_('Click to learn more about {{item}}', { item: i18n.getString(_TL_('End time')) }))}
            />
          </Label>
          <DateTimePicker
            i18n={i18n}
            onDateChange={onEndDateChange}
            selectedDate={selectedEndDate}
            onTimeChange={onEndTimeChange}
            selectedHour={selectedEndTime}
            hideTitle
            datePickerId="EndDatePicker"
            timePickerId="EndTimePicker"
          />
        </div>
      </Stack>
      <Error message={errorMessage} />
    </div>
  );
}

SeasonalityDateTimePicker.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  setSelectedStartDate: PropTypes.func.isRequired,
  selectedStartDate: PropTypes.instanceOf(Date).isRequired,
  setSelectedEndDate: PropTypes.func.isRequired,
  selectedEndDate: PropTypes.instanceOf(Date).isRequired,
  selectedStartTime: PropTypes.string.isRequired,
  selectedEndTime: PropTypes.string.isRequired,
  setSelectedStartTime: PropTypes.func.isRequired,
  setSelectedEndTime: PropTypes.func.isRequired,
  showValidationError: PropTypes.bool.isRequired,
  adjustmentType: PropTypes.oneOf([AdjustmentType.DataExclusion, AdjustmentType.SeasonalityAdjustment]).isRequired,
};

const Wrapped = _.compose(
  withI18nConsumer,
  Comp => withDefaultStyles(Comp, getStyles)
)(SeasonalityDateTimePicker);

export { Wrapped as SeasonalityDateTimePicker };
